import {
  Table as ChakraTable,
  Checkbox,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { Table } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import HeaderElement from './HeaderElement';

type Props<TData> = {
  table: Table<TData>;
  withRowSelection?: boolean;
};

export default function TanstackTableView<TData>({
  table,
  withRowSelection = false,
}: Props<TData>) {
  const tableRef = useRef<HTMLTableElement>(null);
  const { t } = useTranslation();

  return (
    <TableContainer
      bg="white"
      borderRadius={10}
      width="100%"
      maxW="100%"
      display="block"
      border="1px solid rgba(0, 0, 0, 0.1)"
      ref={tableRef}
    >
      <ChakraTable width={table.getTotalSize()} variant="simple" bg="white" borderRadius={4}>
        <Thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={`headerGroup ${headerGroup.id}`} display="flex" width="fit-content">
              {withRowSelection && (
                <Th
                  w="40px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  paddingX="0.5rem"
                >
                  <Checkbox
                    isChecked={table.getIsAllRowsSelected()}
                    isIndeterminate={table.getIsSomeRowsSelected()}
                    onChange={table.getToggleAllRowsSelectedHandler()}
                  />
                </Th>
              )}
              {headerGroup.headers.map((header) => (
                <HeaderElement key={`header ${header.id}`} header={header} />
              ))}
              <Th w="auto" paddingX="1rem" paddingY="0.5rem"></Th>
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.map((row) => (
            <Tr key={`row ${row.id}`} display="flex" width="fit-content">
              {withRowSelection && (
                <Td
                  w="40px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  padding="0.5rem"
                >
                  <Checkbox
                    isChecked={row.getIsSelected()}
                    isIndeterminate={row.getIsSomeSelected()}
                    onChange={row.getToggleSelectedHandler()}
                  />
                </Td>
              )}
              {row.getVisibleCells().map((cell) => (
                <Td
                  key={`cell ${cell.id}`}
                  overflow="hidden"
                  padding="1rem"
                  w={cell.column.getSize()}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              ))}
            </Tr>
          ))}
          {table.getRowModel().rows.length === 0 && (
            <Tr>
              <Td
                colSpan={table.getAllColumns().length + (withRowSelection ? 1 : 0)}
                textAlign="center"
              >
                {t('tables.noData')}
              </Td>
            </Tr>
          )}
        </Tbody>
      </ChakraTable>
    </TableContainer>
  );
}
